import { app } from '../../app.module'
/**
 * アカウント分析のサービス
 * @return {Object} オブジェクト
 */
function AnalyticsData($rootScope) {
  /**
   * setFilterOptions フィルタリングオプションをセット
   * @param {object} startEndDate 期間
   * @param {string} stateName 現在の画面
   * @param {string} accountId アカウントID
   * @return {Object} フィルタリングオプション
   */
  function setFilterOptions(
    startEndDate: {
      startDate: number
      endDate: number
    },
    stateName: string,
    accountId: string
  ) {
    // 開始日(デフォルト: 過去31日)
    let startDate = moment(
      moment(moment().subtract(31, 'days').format('YYYY-MM-DD 00:00:00'))
    ).unix()
    let endDate = moment(moment(moment().subtract(1, 'days').format('YYYY-MM-DD 23:59:59'))).unix()

    // 分析画面以外の場合、開始日は13ヵ月前にする
    if (stateName !== 'analytics') {
      startDate = moment(
        moment(moment().subtract(366, 'days').format('YYYY-MM-01 00:00:00'))
      ).unix()
    }

    // 期間が指定されている場合、セット
    if (startEndDate.startDate !== undefined && startEndDate.endDate !== undefined) {
      startDate = startEndDate.startDate
      endDate = startEndDate.endDate
    }

    const filterOptions = {
      startEndDate: {
        // 開始日
        startDate: startDate,
        // 終了日
        endDate: endDate,
      },
      // メインカテゴリ
      categoryFilter: null,
      // サブカテゴリ
      subCategoryFilter: null,
      // 検索するアカウント
      accountFilter: null,
      // 投稿タイプ
      postTypeFilter:
        accountId === null ? ($rootScope.postTypeFilter ? $rootScope.postTypeFilter : null) : null,
      // キーワード
      messageFilter: accountId === null ? $rootScope.messageFilter : null,
      // 検索するタグ
      tagFilter: accountId === null ? $rootScope.postTagFilter : null,
      // アカウントタグ
      accountTagFilter: accountId === null ? $rootScope.accountTagFilter : null,
      // グローバルアカウント
      accountGlobalFilter: accountId === null ? $rootScope.accountGlobalFilter : null,
      // 検索するハッシュタグ
      hashtagFilter: accountId === null ? $rootScope.hashtagFilter : null,
      // フォロワー数
      followerFilter: accountId === null ? $rootScope.followerFilter : null,
      // instagramフィルタ
      inFilter: accountId === null ? $rootScope.inFilter : null,
      // お気に入り
      favoriteFilter: accountId === null ? $rootScope.favoriteFilter : null,
      groupId: $rootScope.groupId,
      // グループの契約プランがProか否か
      isPro: $rootScope.isPro,
      userId: $rootScope.userId,
      // アカウントID
      accountId: accountId,
    }

    return filterOptions
  }

  /**
   * setOrderObject ソート対象をセット
   * @param {string} order ソート対象
   * @param {boolean} dataPerPost 1投稿にあたりのフラグ
   * @return {Object} ソート対象
   */
  function setOrderObject(order, dataPerPost) {
    const orderArray = order.split('-')
    const dataPerPostArray = [
      'impressions',
      'plays',
      'likes',
      'comments',
      'retweets',
      'quotes',
      'replies',
      'shares',
      'engagements',
      'views',
      'dislikes',
      'durations',
      'collects',
    ]
    let checkOrder = order

    // リバースの場合
    if (orderArray.length > 1) {
      checkOrder = orderArray[1]
    }

    // 1投稿あたりの平均をソートする場合
    if (dataPerPostArray.indexOf(checkOrder) > -1 && dataPerPost === true) {
      order = order + 'PerPost'
    }

    return order
  }

  /**
   * changeFilterData フィルタリングのオプションを変更する
   * @param {string} filterType フィルタータイプ
   * @param {string} filterValue フィルター値
   * @param {Object} getOptions フィルタリングのオプション
   * @return {Object} フィルタリングのオプション
   */
  function changeFilterData(filterType: string, filterValue: string, getOptions) {
    switch (filterType) {
      // 投稿内容でフィルタリング
      case 'keywordFilter':
        getOptions.messageFilter = filterValue
        break
      // フォロワー数でフィルタリング
      case 'followerRangeFilter':
        getOptions.followerFilter = filterValue
        break

      // 動画秒数でフィルタリング
      case 'durationRangeFilter':
        getOptions.durationFilter = filterValue
        break

      // お気に入りのフィルタリング
      case 'favoritePostFilter':
        getOptions.favoriteFilter = filterValue
        break

      // 投稿タイプのフィルタリング
      case 'postTypeFilter':
        getOptions.postTypeFilter = filterValue ? filterValue : null
        break

      // 投稿タグのフィルタリング
      case 'postTagFilter':
        getOptions.tagFilter = filterValue
        break

      // ハッシュタグのフィルタリング
      case 'postHashtagFilter':
        getOptions.hashtagFilter = filterValue
        break

      // アカウントのフィルタリング
      case 'accountFilter':
        getOptions.accountFilter = filterValue
        break

      // サブカテゴリのフィルタリング
      case 'subCategoryFilter':
        getOptions.subCategoryFilter = filterValue
        break

      // instagramフィルタのフィルタリング
      case 'inFilter':
        getOptions.inFilter = filterValue
        break

      // アカウントタグのフィルタリング
      case 'accountTagFilter':
        getOptions.accountTagFilter = filterValue
        break

      // グローバルアカウントのフィルタリング
      case 'accountGlobalFilter':
        getOptions.accountGlobalFilter = filterValue
        break
    }

    return getOptions
  }

  /**
   * orderBy ソートの処理
   * @param {array} accounts アカウント一覧
   * @param {string} order ソート対象
   * @param {boolean} dataPerPost 1投稿にあたりのフラグ
   * @return {Object} ソート結果
   */
  function orderBy(accounts: [string], order: string, dataPerPost: boolean) {
    let reverse = true
    const orderArray = order.split('-')
    const dataPerPostArray = [
      'plays',
      'collects',
      'views',
      'impressions',
      'likes',
      'dislikes',
      'durations',
      'comments',
      'retweets',
      'quotes',
      'replies',
      'shares',
      'engagements',
    ]
    let aSortData: number
    let bSortData: number

    // リバースの場合
    if (orderArray.length > 1) {
      reverse = false
      order = orderArray[1]
    }

    // ソートを行う
    accounts.sort(function (a, b) {
      // 1投稿あたりの平均をソートする場合
      if (dataPerPostArray.indexOf(order) > -1 && dataPerPost === true) {
        aSortData = a[order + 'PerPost']
        bSortData = b[order + 'PerPost']
      } else {
        aSortData = a[order]
        bSortData = b[order]
      }

      return aSortData > bSortData ? 1 : -1
    })

    // リバース
    if (reverse) {
      accounts.reverse()
    }

    return accounts
  }

  return {
    setFilterOptions: setFilterOptions,
    setOrderObject: setOrderObject,
    changeFilterData: changeFilterData,
    orderBy: orderBy,
  }
}

/**
 * AccountAnalyticsService アカウント分析機能関連のサービスを1つのサービスにオブジェクトとして格納
 * @param {Function} AnalyticsData アカウント分析に関するService
 * @return {Object} サービスのオブジェクト
 */
function AccountAnalyticsService(AnalyticsData) {
  return {
    Data: AnalyticsData,
  }
}

// 分析用のサービスを定義
app
  .factory('AccountAnalyticsService', AccountAnalyticsService)
  .factory('AnalyticsData', AnalyticsData)
